import { useState } from "react";
import { KeyRound, Mail, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function AccountModal({ user, onClose, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingCreds, setSavingCreds] = useState(false);

  const saveName = async () => {
    if (!name.trim()) { toast.error("Name can't be empty"); return; }
    setSavingName(true);
    try {
      await api.post("/auth/update-profile", { name: name.trim() });
      toast.success("Name updated");
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update name");
    } finally {
      setSavingName(false);
    }
  };

  const saveCreds = async () => {
    if (!currentPassword) { toast.error("Enter your current password to confirm"); return; }
    if (!newEmail && !newPassword) { toast.error("Enter a new email or new password"); return; }
    if (newPassword && newPassword.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setSavingCreds(true);
    try {
      const body = { current_password: currentPassword };
      if (newEmail) body.new_email = newEmail.trim();
      if (newPassword) body.new_password = newPassword;
      await api.post("/auth/change-credentials", body);
      toast.success("Login details updated");
      setCurrentPassword(""); setNewEmail(""); setNewPassword("");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update login details");
    } finally {
      setSavingCreds(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <section className="create-modal" role="dialog" aria-modal="true" data-testid="account-modal">
        <div className="modal-heading">
          <div>
            <p className="eyebrow">FLOWSPHERE / ACCOUNT</p>
            <h2>Your account</h2>
            <p>Update your display name and your login credentials.</p>
          </div>
          <button className="icon-button" onClick={onClose} data-testid="account-close-button"><X size={18} /></button>
        </div>

        <div className="account-block">
          <div className="account-block-head"><UserRound size={16} /><strong>Display name</strong></div>
          <div className="account-inline">
            <input data-testid="account-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
            <button className="primary-button" disabled={savingName} onClick={saveName} data-testid="account-save-name-button">
              {savingName ? "Saving…" : "Save name"}
            </button>
          </div>
        </div>

        <div className="account-block">
          <div className="account-block-head"><KeyRound size={16} /><strong>Login details</strong></div>
          <p className="account-hint">For your security, enter your current password to change your email or password.</p>
          <label className="modal-field">
            <span><KeyRound size={12} /> Current password</span>
            <input data-testid="account-current-password-input" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" autoComplete="current-password" />
          </label>
          <div className="account-grid">
            <label className="modal-field">
              <span><Mail size={12} /> New email (optional)</span>
              <input data-testid="account-new-email-input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={user?.email} autoComplete="off" />
            </label>
            <label className="modal-field">
              <span><KeyRound size={12} /> New password (optional)</span>
              <input data-testid="account-new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 characters" autoComplete="new-password" />
            </label>
          </div>
          <div className="modal-actions">
            <button className="secondary-button" onClick={onClose}>Cancel</button>
            <button className="primary-button" disabled={savingCreds} onClick={saveCreds} data-testid="account-save-credentials-button">
              {savingCreds ? "Updating…" : "Update login details"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
